import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { WeatherDisplay, WeatherType } from '../WeatherDisplay/WeatherDisplay'; // Adjusted import path
import './DashboardHeader.scss';
import { jwtDecode } from 'jwt-decode';
import { useGeolocation } from 'src/hooks/GeolocationProvider';
import Cookies from 'js-cookie';
import { GoogleJwtPayload } from 'src/types/interfaces';

interface DashboardHeaderProps {
  upcomingPlansCount?: number;
}

const getGreeting = (): string => {
  const social_at = Cookies.get('social_at')
  const userName = social_at ? jwtDecode<GoogleJwtPayload>(social_at).given_name : undefined
  const hour = new Date().getHours();
  let greeting = 'Good ';
  if (hour < 12) {
    greeting += 'morning'
  } else if (hour < 18) {
    greeting += 'afternoon'
  } else {
    greeting += 'evening'
  }
  greeting += (userName ? `, ${userName}!` : '!')
  return greeting;
};

// Function to map WeatherStack weather codes to our app's weather types
const mapWeatherCode = (code: number): WeatherType => {
  // Weather codes based on WeatherStack documentation
  // Sunny or Clear
  if ([113, 116].includes(code)) return 'sunny';
  // Cloudy or Partly Cloudy
  if ([119, 122, 143, 248, 260].includes(code)) return 'cloudy';
  // Rainy conditions
  if ([176, 182, 185, 200, 293, 296, 299, 302, 305, 308, 311, 314, 317,
    320, 350, 353, 356, 359, 362, 365, 368, 371, 374, 377].includes(code))
    return 'rainy';
  // Snowy conditions
  if ([179, 227, 230, 323, 326, 329, 332, 335, 338,
    339, 392, 395, 179, 227, 230].includes(code))
    return 'snowy';
  // Partly cloudy as default fallback
  return 'partlyCloudy';
};

// Fetch real weather data from WeatherStack API
const fetchWeatherData = async (
  userLocation?: google.maps.LatLngLiteral | null
): Promise<{ type: WeatherType; temperature: number; location: string }> => {
  try {
    const apiKey = process.env.REACT_APP_WEATHERSTACK_API_KEY || '';

    // Use user location if available, otherwise fallback to Manhattan
    let query = 'Manhattan,New+York';
    let locationName = 'Manhattan, NYC';

    if (userLocation) {
      query = `${userLocation.lat},${userLocation.lng}`;
      locationName = 'Your Location';
    }

    const response = await fetch(
      `https://api.weatherstack.com/current?access_key=${apiKey}&query=${query}&units=f`
    );

    if (!response.ok) {
      throw new Error('Weather API request failed');
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.info || 'Weather API error');
    }

    // If we used user location and got a proper response, use the location from API
    // Otherwise use our fallback location name
    const finalLocationName = userLocation && data.location?.name
      ? data.location.name
      : locationName;

    return {
      type: mapWeatherCode(data.current.weather_code),
      temperature: data.current.temperature,
      location: finalLocationName
    };
  } catch (error) {
    console.error('Error fetching weather data:', error);
    // Fallback data in case of API failure - always use Manhattan as fallback
    return {
      type: 'partlyCloudy',
      temperature: 72, // Fallback temperature in Fahrenheit
      location: 'Manhattan, NYC'
    };
  }
};

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ upcomingPlansCount = 0 }) => {
  const { userGeolocation } = useGeolocation();
  const [greeting, setGreeting] = useState<string>(getGreeting());
  const [weather, setWeather] = useState<{ type: WeatherType; temperature: number; location: string } | null>(null);
  const [loadingWeather, setLoadingWeather] = useState<boolean>(true);

  useEffect(() => {
    fetchWeatherData(userGeolocation)
      .then(data => {
        setWeather(data);
        setLoadingWeather(false);
      })
      .catch(error => {
        console.error("Failed to fetch weather:", error);
        setLoadingWeather(false);
        // Set default/error state for weather
        setWeather({ type: 'partlyCloudy', temperature: 0, location: 'Error' });
      });

    // Optional: Set interval to update greeting if needed (e.g., if app runs across time boundaries)
    // const intervalId = setInterval(() => setGreeting(getGreeting()), 60000); // Update every minute
    // return () => clearInterval(intervalId);

  }, [userGeolocation]); // Re-fetch weather when user geolocation changes

  const subGreeting = upcomingPlansCount > 0
    ? `You have ${upcomingPlansCount} upcoming plan${upcomingPlansCount > 1 ? 's' : ''} — enjoy your ${greeting ? greeting.split(' ')[1]?.toLowerCase().replace(',', '') : 'day'}!`
    : `Go and explore some new and exciting places today!`;

  return (
    <Box className="dashboardHeader">
      <Box className="greetingSection">
        <Typography variant="h1" className="greetingText" gutterBottom>
          {greeting}
        </Typography>
        <Typography variant="body1" className="subGreetingText">
          {subGreeting}
        </Typography>
      </Box>

      <Box className="weatherSection">
        {loadingWeather ? (
          <Typography>Loading weather...</Typography> // Simple loading indicator
        ) : weather ? (
          // Pass the location as the description string directly
          <WeatherDisplay
            type={weather.type}
            temperature={weather.temperature}
            location={weather.location} // Use location for the 'Sunny' text
          />
        ) : (
          <Typography>Weather unavailable</Typography> // Error state
        )}
      </Box>
    </Box>
  );
};      